/**
 * Logo Storage Utility
 * Manages persistent logo storage in localStorage with deduplication support
 */

import { logger } from './logger';

const DEFAULT_LOGO_KEY = 'default-invoice-logo';
const LOGO_HASHES_KEY = 'invoice-logo-hashes';

/**
 * Calculates a simple hash from a base64 string for deduplication
 */
function hashLogo(logoData: string): string {
  // Remove the data:image/...;base64, prefix if present
  const base64Data = logoData.split(',')[1] || logoData;

  // Simple hash function for base64 strings
  let hash = 0;
  for (let i = 0; i < base64Data.length; i++) {
    const char = base64Data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return Math.abs(hash).toString(36);
}

/**
 * Retrieves the default logo from localStorage
 * @returns Base64 encoded logo data or null if not set
 */
export function getDefaultLogo(): string | null {
  try {
    const logo = localStorage.getItem(DEFAULT_LOGO_KEY);
    logger.info('[logoStorage] getDefaultLogo:', logo ? 'Found' : 'Not found');
    return logo;
  } catch (error) {
    logger.error('[logoStorage] Failed to retrieve default logo:', error);
    return null;
  }
}

/**
 * Saves a logo as the default logo in localStorage
 * @param logoData - Base64 encoded logo data
 * @returns The hash of the stored logo
 */
export function setDefaultLogo(logoData: string): string {
  try {
    const logoHash = hashLogo(logoData);
    logger.info('[logoStorage] setDefaultLogo: Saving logo with hash:', logoHash);

    // Store the logo
    localStorage.setItem(DEFAULT_LOGO_KEY, logoData);

    // Update the hash registry
    const hashes = getLogoHashes();
    hashes[logoHash] = DEFAULT_LOGO_KEY;
    localStorage.setItem(LOGO_HASHES_KEY, JSON.stringify(hashes));

    logger.info('[logoStorage] Default logo saved successfully');
    return logoHash;
  } catch (error) {
    logger.error('[logoStorage] Failed to save default logo:', error);
    throw error;
  }
}

/**
 * Removes the default logo from localStorage
 */
export function clearDefaultLogo(): void {
  try {
    const currentLogo = localStorage.getItem(DEFAULT_LOGO_KEY);
    logger.info('[logoStorage] clearDefaultLogo: Clearing logo');

    if (currentLogo) {
      const logoHash = hashLogo(currentLogo);

      // Remove the logo
      localStorage.removeItem(DEFAULT_LOGO_KEY);

      // Update the hash registry
      const hashes = getLogoHashes();
      delete hashes[logoHash];
      localStorage.setItem(LOGO_HASHES_KEY, JSON.stringify(hashes));

      logger.info('[logoStorage] Default logo cleared successfully');
    }
  } catch (error) {
    logger.error('[logoStorage] Failed to clear default logo:', error);
  }
}

/**
 * Checks if a logo matches the default logo
 * @param logoData - Base64 encoded logo data to check
 * @returns true if the logo matches the default logo
 */
export function isDefaultLogo(logoData: string): boolean {
  const defaultLogo = getDefaultLogo();
  if (!defaultLogo || !logoData) {
    return false;
  }

  const logoHash = hashLogo(logoData);
  const defaultHash = hashLogo(defaultLogo);

  return logoHash === defaultHash;
}

/**
 * Gets the hash of a logo for storage optimization
 * @param logoData - Base64 encoded logo data
 * @returns The hash of the logo
 */
export function getLogoHash(logoData: string): string {
  return hashLogo(logoData);
}

/**
 * Retrieves all logo hashes from localStorage
 * @returns Object mapping logo hashes to storage keys
 */
function getLogoHashes(): Record<string, string> {
  try {
    const hashesJson = localStorage.getItem(LOGO_HASHES_KEY);
    return hashesJson ? JSON.parse(hashesJson) : {};
  } catch (error) {
    console.error('Failed to retrieve logo hashes from localStorage:', error);
    return {};
  }
}

/**
 * Checks if a logo already exists in storage (for deduplication)
 * @param logoData - Base64 encoded logo data to check
 * @returns The storage key if the logo exists, null otherwise
 */
export function logoExistsInStorage(logoData: string): string | null {
  const logoHash = hashLogo(logoData);
  const hashes = getLogoHashes();

  return hashes[logoHash] || null;
}
